import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Trophy, Users, AlertTriangle, Building2,
  CheckCircle, XCircle, ChevronRight, ExternalLink, Zap, TrendingUp, MapPin,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const RESULT = {
  date: "Saturday, May 3, 2025",
  winner: "Australian Labor Party",
  pm: "Anthony Albanese",
  type: "Federal Election — 151 House of Representatives seats",
  seats: [
    { party: "ALP", label: "Australian Labor Party", seats: 87, color: "#CC2529", change: "+15", majority: true },
    { party: "Coalition", label: "Liberal–National Coalition", seats: 48, color: "#1B4F9E", change: "-14", majority: false },
    { party: "Greens", label: "The Greens", seats: 4, color: "#10B981", change: "-1 (lost Melbourne)", majority: false },
    { party: "Teals", label: "Teal Independents", seats: 8, color: "#0D9488", change: "held all", majority: false },
    { party: "Others", label: "Other Independents & Minors", seats: 4, color: "#94A3B8", change: "", majority: false },
  ],
  two_pp: { alp: 54.3, coalition: 45.7 },
  turnout: 91.2,
  enrolled: 17_800_000,
};

const LEADERS = [
  {
    id: "anthony-albanese",
    name: "Anthony Albanese",
    role: "Prime Minister — re-elected for second term",
    party: "Australian Labor Party",
    color: "#CC2529",
    outcome: "WON",
    outcomeColor: "#10B981",
    electorate: "Grayndler (NSW)",
    highlights: [
      "Won historic second term with increased majority (87 seats)",
      "NDIS reform, Housing Australia Future Fund passed",
      "Cost-of-living relief: $300 energy rebates, tax cuts stage 3 revised",
      "AUKUS nuclear submarine deal progressing at Osborne",
    ],
    criticisms: [
      "Housing crisis worsened despite commitments",
      "Inflation remained sticky longer than promised",
      "Voice referendum defeat in October 2023",
    ],
  },
  {
    id: "peter-dutton",
    name: "Peter Dutton",
    role: "Former Liberal Leader — lost election, resigned as leader",
    party: "Liberal Party of Australia",
    color: "#1B4F9E",
    outcome: "LOST",
    outcomeColor: "#EF4444",
    electorate: "Dickson (QLD)",
    highlights: [
      "Campaigned on nuclear energy as cheaper alternative",
      "Pushed harder border protection stance",
      "Opposed Voice to Parliament referendum",
      "Strong on cost-of-living messaging in opposition",
    ],
    criticisms: [
      "Nuclear plan widely seen as too slow and costly",
      "Lost Dickson seat personally to Labor",
      "Party suffered historic losses including more Teal defections",
    ],
  },
];

const ISSUES = [
  {
    id: "housing",
    icon: "🏠",
    title: "Housing Affordability",
    color: "#DC2626",
    result: "Labor delivered Housing Australia Future Fund ($10B), Help to Buy scheme. Prices still rose significantly in major cities.",
    alp: "Housing Australia Future Fund, Help to Buy, build-to-rent tax changes",
    coalition: "Opposed HAFF, preferred super withdrawal for first home buyers",
    verdict: "Labor passed key housing bills but affordability worsened — now a major 2nd-term challenge.",
  },
  {
    id: "cost-of-living",
    icon: "💰",
    title: "Cost of Living",
    color: "#F59E0B",
    result: "Labor delivered $300 energy rebates, revised Stage 3 tax cuts, HELP debt indexation relief.",
    alp: "$300 energy rebate, revised Stage 3 cuts benefiting lower earners, bulk billing triple",
    coalition: "Original Stage 3 cuts intact, abolish electric vehicle mandates, freeze fuel excise",
    verdict: "Labor's revised tax cuts and energy rebates resonated. Coalition's Stage 3 defence became a liability.",
  },
  {
    id: "climate",
    icon: "🌍",
    title: "Climate & Energy",
    color: "#10B981",
    result: "Labor legislated 43% emission reduction by 2030. Coalition countered with nuclear energy plan for 7 reactors.",
    alp: "43% reduction by 2030, Capacity Investment Scheme, rooftop solar incentives",
    coalition: "7 nuclear reactors, oppose renewable mandates, 'get out of the way of gas'",
    verdict: "Labor's renewables plan vs Coalition nuclear became the defining policy battle of 2025.",
  },
  {
    id: "aukus",
    icon: "🛳️",
    title: "AUKUS & Defence",
    color: "#1B4F9E",
    result: "Bipartisan AUKUS support. Labor progressed optimal pathway. Virginia-class submarines deal confirmed.",
    alp: "Full AUKUS commitment, workforce development at Henderson and Osborne shipyards",
    coalition: "Supported AUKUS but criticised timeline as too slow",
    verdict: "AUKUS — rare bipartisan policy. A generational economic winner for SA and WA.",
  },
  {
    id: "healthcare",
    icon: "🏥",
    title: "Health & Medicare",
    color: "#7C3AED",
    result: "Labor tripled bulk billing incentive. GP bulk billing rates rose from 78% to 82%. Mental health funding expanded.",
    alp: "Tripled bulk billing incentive, 50 urgent care clinics, Strengthening Medicare",
    coalition: "Opposed bulk billing incentive as a 'blunt instrument', preferred Medibank reforms",
    verdict: "Medicare investment was Labor's strongest first-term domestic achievement.",
  },
  {
    id: "integrity",
    icon: "⚖️",
    title: "Integrity & NACC",
    color: "#2E5948",
    result: "Labor established the National Anti-Corruption Commission (NACC). Multiple referrals under investigation.",
    alp: "Passed NACC legislation, improved parliamentary standards, lobbyist register reform",
    coalition: "Backed weaker NACC model, opposed some referral powers",
    verdict: "NACC was Labor's integrity flagship — and a direct response to the Morrison government's promises.",
  },
  {
    id: "indigenous",
    icon: "🦘",
    title: "Indigenous Affairs",
    color: "#D97706",
    result: "Voice to Parliament referendum failed in October 2023. Labor pivoted to Closing the Gap targets and Treaty conversations.",
    alp: "Voice referendum (failed), Closing the Gap commitments, Makarrata process",
    coalition: "Opposed Voice ('No' campaign). Supported practical programs without constitutional change.",
    verdict: "The Voice defeat was Albanese's biggest political setback. Still a defining national conversation.",
  },
  {
    id: "immigration",
    icon: "✈️",
    title: "Immigration",
    color: "#94A3B8",
    result: "Net overseas migration hit record 518,000 in 2022-23. Labor tightened international student visa caps in 2024.",
    alp: "Record NOM driven by post-COVID recovery and international students; tightened caps in 2024",
    coalition: "Promised to halve net migration to 160,000/year, tighten student visas immediately",
    verdict: "Immigration became a major election issue. Labor's late course-correction wasn't enough for critics.",
  },
];

const NOTABLE_RESULTS = [
  { seat: "Kooyong (VIC)", result: "Monique Ryan (Teal) held", color: "#0D9488", note: "Beat Liberal challenger Amelia Hamer — Teal wave held" },
  { seat: "Melbourne (VIC)", result: "ALP won from Greens", color: "#CC2529", note: "Adam Bandt lost his seat — end of an era for the Greens" },
  { seat: "Warringah (NSW)", result: "Zali Steggall held", color: "#0D9488", note: "Third consecutive win for the Northern Beaches independent" },
  { seat: "Dickson (QLD)", result: "Labor flipped — Dutton lost seat", color: "#CC2529", note: "Peter Dutton personally lost his seat — rare for an opposition leader" },
  { seat: "Wentworth (NSW)", result: "Allegra Spender (Teal) held", color: "#0D9488", note: "Eastern Sydney Teal seat retained comfortably" },
  { seat: "Herbert (QLD)", result: "ALP held by 1.4%", color: "#CC2529", note: "Townsville's ultra-marginal seat stayed Labor by a whisker" },
  { seat: "Eden-Monaro (NSW)", result: "ALP held (bellwether worked)", color: "#CC2529", note: "Bellwether voted with the winner — again" },
  { seat: "Mayo (SA)", result: "Rebekha Sharkie (CA) held", color: "#0D9488", note: "Centre Alliance retained the Adelaide Hills seat comfortably" },
];

export default function Federal2025Page() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="federal2025-page">

      {/* Hero */}
      <div className="border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="h-2" style={{ backgroundColor: "#CC2529" }} />
        <div className="max-w-5xl mx-auto px-4 py-10">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-6 hover:opacity-75"
            style={{ color: "#FFC82E" }}
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs font-black px-3 py-1 border-2 border-black" style={{ backgroundColor: "#CC2529", color: "#fff" }}>
              FEDERAL 2025
            </span>
            <span className="text-xs font-black px-2 py-1 border-2 border-black" style={{ backgroundColor: "#10B981", color: "#fff" }}>
              LABOR MAJORITY
            </span>
            <span className="text-xs font-black px-2 py-1 border-2 border-black" style={{ backgroundColor: "#FFC82E", color: "#000" }}>
              RESULTS GUIDE
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Australian Federal<br />Election 2025
          </h1>
          <p className="text-base mb-8" style={{ color: "#94A3B8" }}>
            {RESULT.date} · 151 House of Representatives seats · {RESULT.enrolled.toLocaleString()} Australians enrolled
          </p>

          {/* Result summary */}
          <div className="border-2 border-black p-5 mb-6" style={{ backgroundColor: "#1E293B", boxShadow: "6px 6px 0 #CC2529" }}>
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="w-6 h-6" style={{ color: "#FFC82E" }} />
              <span className="text-lg font-black text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                Labor wins majority — Albanese re-elected PM
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {RESULT.seats.map((s) => (
                <div
                  key={s.party}
                  className="border-2 border-black p-3 text-center"
                  style={{ backgroundColor: s.majority ? s.color : "#0F172A", boxShadow: s.majority ? `3px 3px 0 #000` : "none" }}
                  data-testid={`result-${s.party.toLowerCase()}`}
                >
                  <div className="text-2xl font-black" style={{ color: s.majority ? "#fff" : s.color, fontFamily: "Outfit, sans-serif" }}>
                    {s.seats}
                  </div>
                  <div className="text-xs font-black text-white">{s.party}</div>
                  {s.change && <div className="text-xs mt-0.5" style={{ color: s.majority ? "rgba(255,255,255,0.7)" : "#64748B" }}>{s.change}</div>}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div>
                <span className="font-black" style={{ color: "#CC2529" }}>2PP: ALP {RESULT.two_pp.alp}%</span>
                <span className="mx-2" style={{ color: "#475569" }}>vs</span>
                <span className="font-black" style={{ color: "#1B4F9E" }}>Coalition {RESULT.two_pp.coalition}%</span>
              </div>
              <div style={{ color: "#94A3B8" }}>
                Turnout: <span className="font-black text-white">{RESULT.turnout}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-14">

        {/* Leaders */}
        <section data-testid="leaders-section">
          <h2 className="text-2xl font-black mb-6 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            <Users className="w-6 h-6" style={{ color: "#FFC82E" }} />
            The Leaders: Where Are They Now?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {LEADERS.map((l) => (
              <div
                key={l.id}
                className="border-2 border-black overflow-hidden"
                style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
                data-testid={`leader-card-${l.id}`}
              >
                <div className="h-2" style={{ backgroundColor: l.color }} />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-xl font-black mb-1" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{l.name}</h3>
                      <span
                        className="text-xs font-black px-2 py-0.5 border-2 border-black text-white"
                        style={{ backgroundColor: l.color }}
                      >
                        {l.party}
                      </span>
                    </div>
                    <span
                      className="flex-shrink-0 text-xs font-black px-2 py-1 border-2 border-black"
                      style={{ backgroundColor: l.outcomeColor, color: "#fff", boxShadow: "2px 2px 0 #000" }}
                    >
                      {l.outcome}
                    </span>
                  </div>
                  <p className="text-xs mb-4" style={{ color: textSec }}>{l.role} · {l.electorate}</p>

                  <div className="mb-3">
                    <p className="text-xs font-black mb-2" style={{ color: "#10B981" }}>Key achievements / positions:</p>
                    {l.highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-1.5 mb-1">
                        <CheckCircle className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: "#10B981" }} />
                        <span className="text-xs" style={{ color: text }}>{h}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-black mb-2" style={{ color: "#EF4444" }}>Criticisms / failures:</p>
                    {l.criticisms.map((c, i) => (
                      <div key={i} className="flex items-start gap-1.5 mb-1">
                        <XCircle className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: "#EF4444" }} />
                        <span className="text-xs" style={{ color: text }}>{c}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate(`/politicians/${l.id}`)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 border-2 border-black text-xs font-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: l.color, color: "#fff", boxShadow: "2px 2px 0 #000" }}
                    data-testid={`view-profile-${l.id}`}
                  >
                    View Full Profile + Promise Tracker <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 8 Key Issues */}
        <section data-testid="issues-section">
          <h2 className="text-2xl font-black mb-6 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            <AlertTriangle className="w-6 h-6" style={{ color: "#FFC82E" }} />
            8 Issues That Decided the 2025 Election
          </h2>
          <div className="space-y-4">
            {ISSUES.map((issue) => (
              <div
                key={issue.id}
                className="border-2 border-black overflow-hidden"
                style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                data-testid={`issue-${issue.id}`}
              >
                <div className="h-1.5" style={{ backgroundColor: issue.color }} />
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">{issue.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-base font-black mb-1" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{issue.title}</h3>
                      <p className="text-sm" style={{ color: textSec }}>{issue.result}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div className="border-l-4 pl-3" style={{ borderColor: "#CC2529" }}>
                      <p className="text-xs font-black mb-1" style={{ color: "#CC2529" }}>Labor policy</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.alp}</p>
                    </div>
                    <div className="border-l-4 pl-3" style={{ borderColor: "#1B4F9E" }}>
                      <p className="text-xs font-black mb-1" style={{ color: "#1B4F9E" }}>Coalition policy</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.coalition}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 border border-black" style={{ backgroundColor: isDark ? "#0F172A" : "#FFF9E6" }}>
                    <Zap className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#FFC82E" }} />
                    <p className="text-xs font-bold" style={{ color: textSec }}>{issue.verdict}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Notable seat results */}
        <section data-testid="notable-results-section">
          <h2 className="text-2xl font-black mb-5 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            <TrendingUp className="w-6 h-6" style={{ color: "#FFC82E" }} />
            Notable Seat Results
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NOTABLE_RESULTS.map((r) => (
              <div
                key={r.seat}
                className="flex items-start gap-3 border-2 border-black p-3"
                style={{ backgroundColor: surface, boxShadow: "2px 2px 0 #000" }}
                data-testid={`notable-${r.seat.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <div className="w-2 h-2 flex-shrink-0 mt-1.5 border border-black" style={{ backgroundColor: r.color }} />
                <div>
                  <p className="text-sm font-black" style={{ color: text }}>{r.seat}</p>
                  <p className="text-xs font-bold" style={{ color: r.color }}>{r.result}</p>
                  <p className="text-xs" style={{ color: textSec }}>{r.note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Party performance */}
        <section data-testid="party-performance-section">
          <h2 className="text-2xl font-black mb-5 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            <Building2 className="w-6 h-6" style={{ color: "#FFC82E" }} />
            Party Performance Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-2 border-black text-sm">
              <thead>
                <tr style={{ backgroundColor: "#0F172A", color: "#fff" }}>
                  {["Party", "Seats Won", "Change", "Primary Vote", "2PP", "Verdict"].map((h) => (
                    <th key={h} className="text-left py-2 px-3 text-xs font-black border-b-2 border-black">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { party: "ALP", color: "#CC2529", seats: 87, change: "+15", primary: "34.7%", tpp: "54.3%", verdict: "Historic second-term majority" },
                  { party: "Liberal–National", color: "#1B4F9E", seats: 48, change: "−14", primary: "31.2%", tpp: "45.7%", verdict: "Worst result in modern history" },
                  { party: "The Greens", color: "#10B981", seats: 4, change: "−1", primary: "12.8%", tpp: "—", verdict: "Lost Melbourne; held Brisbane seats" },
                  { party: "Teal Independents", color: "#0D9488", seats: 8, change: "Held all", primary: "~5%", tpp: "—", verdict: "Teal wave consolidated" },
                  { party: "Others / Ind.", color: "#94A3B8", seats: 4, change: "+1", primary: "15.3%", tpp: "—", verdict: "Wilkie, Le, Sharkie, new Ind." },
                ].map((row, i) => (
                  <tr key={row.party} style={{ backgroundColor: i % 2 === 0 ? surface : (isDark ? "#162032" : "#F8FAFC") }}>
                    <td className="py-2 px-3 font-black border-b border-black" style={{ color: row.color }}>{row.party}</td>
                    <td className="py-2 px-3 border-b border-black font-black text-center" style={{ color: text }}>{row.seats}</td>
                    <td className="py-2 px-3 border-b border-black text-xs font-bold" style={{ color: row.change.startsWith("+") ? "#10B981" : row.change.startsWith("−") ? "#EF4444" : "#94A3B8" }}>{row.change}</td>
                    <td className="py-2 px-3 border-b border-black text-xs" style={{ color: textSec }}>{row.primary}</td>
                    <td className="py-2 px-3 border-b border-black text-xs font-bold" style={{ color: text }}>{row.tpp}</td>
                    <td className="py-2 px-3 border-b border-black text-xs" style={{ color: textSec }}>{row.verdict}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Quick tools */}
        <section data-testid="quick-tools-section">
          <h2 className="text-2xl font-black mb-5" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            Explore Further
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Federal Politicians", desc: "Track how they vote & promises", path: "/politicians?level=federal", color: "#CC2529" },
              { label: "Promise Tracker", desc: "2nd term Albanese promises", path: "/promises", color: "#2E5948" },
              { label: "Leaderboard", desc: "Who delivered most?", path: "/leaderboard", color: "#D97706" },
              { label: "Electorate Finder", desc: "Your seat & 2025 results", path: "/electorate", color: "#1B4F9E" },
            ].map((t) => (
              <button
                key={t.label}
                onClick={() => navigate(t.path)}
                className="text-left border-2 border-black p-4 hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                data-testid={`tool-${t.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <p className="font-black text-sm mb-1" style={{ color: t.color }}>{t.label}</p>
                <p className="text-xs" style={{ color: textSec }}>{t.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Official source */}
        <div
          className="border-2 border-black p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ backgroundColor: isDark ? "#1E293B" : "#FFF9E6" }}
        >
          <div>
            <p className="font-black text-sm mb-1" style={{ color: text }}>Official Results — Australian Electoral Commission</p>
            <p className="text-xs" style={{ color: textSec }}>Complete seat-by-seat results, enrolment data, and full candidate tallies at aec.gov.au</p>
          </div>
          <a
            href="https://www.aec.gov.au/elections/federal_elections/2025/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-black px-4 py-2 border-2 border-black hover:-translate-y-0.5 transition-transform"
            style={{ backgroundColor: "#CC2529", color: "#fff", boxShadow: "2px 2px 0 #000" }}
            data-testid="aec-link"
          >
            aec.gov.au <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
